<script setup lang="ts">
import DiagnosisCard from '@/components/molecules/DiagnosisCard.vue'
import DiagnosisCardContent from '@/components/molecules/DiagnosisCardContent.vue'
import { DiagnosisType } from '@/types/schema.generated'

defineProps<{ type: DiagnosisType }>()

const contents: Record<
  keyof typeof DiagnosisType,
  { heading: string; body: string }
> = {
  A: {
    heading: '老後の生活にやや不安あり',
    body: '老後生活の資金にやや不安が残ります。家計の収入が支出を上回っていれば当面の心配はありません。ですが定年後世帯には突然の医療費や介護費の増大によって家計が逼迫するリスクもあります。いざという時に困らないよう、契約している保険の保障内容を確認しておきましょう。',
  },
  B: {
    heading: '備えは十分、安定の老後生活',
    body: '老後生活に向けた資金的な備えができています。ですが定年後世帯には突然の医療費や介護費の増大によって家計が逼迫するリスクもあります。いざという時に困らないよう、契約している保険の保障内容を確認しておきましょう。保有資産が大きい場合は、遺産や相続に関するプランニングも重要です。オカネコのアドバイザーへの無料相談をぜひお待ちしております。',
  },
  C: {
    heading: '教育資金が不足するかも…',
    body: 'お子様の進学に伴う教育費増加で家計が赤字になり、資金が不足するリスクがあります。より詳しいお話を伺ってみないと正確な状況はわかりませんので、ぜひオカネコのアドバイザーに無料相談してみてください。一般論としては支出を見極め、節約して家計の固定費を削減する必要がありそうです。',
  },
  D: {
    heading: '老後資金の備えが必要',
    body: '十分な金額の老後資金を準備できないリスクがあります。より詳しいお話を伺ってみないと正確な状況はわかりませんので、ぜひオカネコのアドバイザーに無料相談してみてください。一般論としては支出を見極め、節約して家計の固定費を削減したり、長期的な視野で資産運用をすることで、老後資金を着実に増やす必要がありそうです。',
  },
  E: {
    heading: '無駄な支出が多いかも？',
    body: '年収の高さに比べて資産が少ないように見えます。年収は平均以上の水準にあるため、家計の固定費を削減し、無駄な支出を抑えることで資産形成を加速させることができます。そうして確保したキャッシュを投資信託や株式、債券、不動産投資などの運用商品を活用して増やすことも検討してみてください。家計改善と資産運用についてはオカネコアドバイザーへの無料相談をぜひ。お待ちしています。',
  },
  F: {
    heading: '今こそ投資デビューの時！',
    body: '平均以上に資産形成が進んでおり素晴らしいです。その一方で投資をしていないことが気になります。資産を預貯金で持つことが必ずしもリスク回避になるわけではありません。昨今のように物価が継続的に上がっていく状況下では実質的なお金の価値が目減りするインフレリスクがあります。投資信託や株式、不動産投資などの運用商品を活用して増やすことも検討しましょう。ぜひオカネコのアドバイザーへ無料相談してください。投資信託や株式、債券、不動産投資といった手法を組み合わせ、お客様のご希望に沿った資産運用のご説明、ご提案を無料で承っています。',
  },
  G: {
    heading: '資産運用比率が保守的かも？',
    body: '平均以上に資産形成が進んでおり素晴らしいです。その一方で資産運用比率が平均より随分低いことが気になります。資産を預貯金で持つことが必ずしもリスク回避になるわけではありません。昨今のように物価が継続的に上がっていく状況下では実質的なお金の価値が目減りするインフレリスクがあります。投資信託や株式、不動産投資などの運用商品を活用して増やすことも検討しましょう。ぜひオカネコのアドバイザーへ無料相談してください。投資信託や株式、債券、不動産投資といった手法を組み合わせ、お客様のご希望に沿った資産運用のご説明、ご提案を無料で承っています。',
  },
  H: {
    heading: '準備は万全！ハイレベル家計',
    body: '今後に向けた資金の準備は万全。資産運用も活用しているかなりハイレベルな家計です。もしこれまでプロのアドバイスを受けずにやってこられたのだとしたら素晴らしい成果ですが、この機会にオカネコのアドバイザーによる無料提案を受けることをお勧めいたします。オカネコのアドバイザーは投資信託や株式、債券、不動産投資といった手法を組み合わせ、お客様のご希望に沿った資産運用のご説明、ご提案を無料で承っています。また、資産運用に限らず、住宅取得や相続等についてのアドバイスも可能です。少しでも気になるテーマがあるようなら、ぜひ一度、ご相談ください。',
  },
} as const
</script>

<template>
  <DiagnosisCard heading="家計状況コメント" :text="contents[type].body">
    <template #content>
      <DiagnosisCardContent>
        <template #item>
          <p :class="$style.content">{{ contents[type].heading }}</p>
        </template>
      </DiagnosisCardContent>
    </template>
  </DiagnosisCard>
</template>

<style lang="scss" module>
.content {
  font-size: 14px;
  font-weight: bold;
}
</style>
